import { IPublicClientApplication } from '@azure/msal-browser';
import { authenticateOnScope, checkValidResponse, scopeApi } from '../services/common';

export interface StatusHistory {
    isForgerockStatusActive: boolean,
    targetEnv: string,
    urn: string,
    protocol: string,
    oldMigrationStatus: string,
    newMigrationStatus: string,
    appInstanceId: string,
    entraAppId: string,
    statusChangedDate: Date,
    statusChangedEmail: string
}

export interface ApplicationItem {
    name: string | null;
    authenticationMethod: string | null;
    status: string | null;
    statusDescription: string | null;
    urn: string | null;
    owner: string | null;
    ownerName: string | null;
    hasExternalUsers: boolean | null;
    isArrStatusValid: boolean | null;
    arrStatus: string | null;
    hasEntraAdminAccount: boolean | null;
    isForgerockStatusValid: boolean | null;
    intAuthLevel: string | null;
    extAuthLevel: string | null;
    isGum: boolean | null;
    isLdap: boolean | null;
    territoryName: string | null;
    ci: string | null;
    snowArrNumber: string | null;
    deliverManagerName: string | null;
    migrationDate: Date | null;
    idasTeamEmail: string | null;
    idasTeamName: string | null;
    listedContact: string | null;
    comments: string | null;
    entraAppId: string | null;
    entraAppTenantId: string | null;
    disabledForOwner: boolean | null;
    ownerCanEditStatus: boolean | null;
    originalStatus: string | null;
    appInstanceId: string | null;
    targetEnvironment: string | null;
    failedDate: Date | null;
    failedReason: string | null;
    ownerConsentDate: Date | null;
    ownerApprovalDate: Date | null;
    entraAppEnv: string | null;
    statusChangedDate: Date | null;
    appDelegate1: string | null;
    appDelegate2: string | null;
    appDelegate1Name: string | null;
    appDelegate2Name: string | null;
    entraIdProjectStatus: string | null;
    primaryOwnerEmail: string | null;
    secondaryOwnerEmail: string | null;
    itApplicationOwnerEmail: string | null;
    memberFirm: string | null;
    statusHistory: StatusHistory[] | null;
}

export const loadApplications = async (instance: IPublicClientApplication, myAppsOnly: boolean): Promise<ApplicationItem[]> => {
    const tokenResponse = await authenticateOnScope(instance, scopeApi);
    const response = await fetch('/app/getApplications', {
        method: 'GET',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'x-my-apps-only': String(myAppsOnly),
            'Content-Type': 'application/json'
        })
    });
    await checkValidResponse(response, "loadApplications");
    const data = await response.json();
    return data;
};

export const getStatusTransitions = async (instance: IPublicClientApplication): Promise<Map<string, string[]>> => {
    const tokenResponse = await authenticateOnScope(instance, scopeApi);
    const response = await fetch('/app/getStatusTransitions', {
        method: 'GET',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'Content-Type': 'application/json'
        })
    });
    await checkValidResponse(response, "getStatusTransitions");
    const data = await response.json();
    return data;
};

export const updateApplication = async (instance: IPublicClientApplication, app: ApplicationItem): Promise<ApplicationItem> => {
    const tokenResponse = await authenticateOnScope(instance, scopeApi);
    const messageObj = JSON.stringify(app);
    const response = await fetch('/app/updateApplication', {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'Content-Type': 'application/json'
        }),
        body: messageObj
    });
    await checkValidResponse(response, "updateApplication");
    const data = await response.json();
    return data;
};

export const confirmMetadataRetrieval = async (instance: IPublicClientApplication, appId: string): Promise<void> => {
    const tokenResponse = await authenticateOnScope(instance, scopeApi);
    const response = await fetch('/app/confirmMetadataRetrieval', {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'Content-Type': 'application/json'
        }),
        body: appId
    });
    await checkValidResponse(response, "confirmMetadataRetrieval");
};

export const approveAppMigration = async (instance: IPublicClientApplication, appId: string): Promise<ApplicationItem> => {
    const tokenResponse = await authenticateOnScope(instance, scopeApi);
    const response = await fetch('/app/approveAppMigration', {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'Content-Type': 'application/json'
        }),
        body: appId
    });
    await checkValidResponse(response, "approveAppMigration");
    const data = await response.json();
    return data;
};

export const mapApplication = async (source: ApplicationItem | null, destination: ApplicationItem | null): Promise<void> => {
    if (destination == null || source == null) {
        return;
    }
    destination.name = source.name;
    destination.ci = source.ci;
    destination.authenticationMethod = source.authenticationMethod;
    destination.status = source.status;
    destination.statusDescription = source.statusDescription;
    destination.urn = source.urn;
    destination.owner = source.owner;
    destination.ownerName = source.ownerName;
    destination.hasExternalUsers = source.hasExternalUsers;
    destination.hasEntraAdminAccount = source.hasEntraAdminAccount;
    destination.isForgerockStatusValid = source.isForgerockStatusValid;
    destination.isArrStatusValid = source.isArrStatusValid;
    destination.arrStatus = source.arrStatus;
    destination.intAuthLevel = source.intAuthLevel;
    destination.extAuthLevel = source.extAuthLevel;
    destination.isGum = source.isGum;
    destination.isLdap = source.isLdap;
    destination.territoryName = source.territoryName;
    destination.snowArrNumber = source.snowArrNumber;
    destination.deliverManagerName = source.deliverManagerName;
    destination.migrationDate = source.migrationDate;
    destination.idasTeamEmail = source.idasTeamEmail;
    destination.idasTeamName = source.idasTeamName;
    destination.listedContact = source.listedContact;
    destination.comments = source.comments;
    destination.entraAppId = source.entraAppId;
    destination.entraAppTenantId = source.entraAppTenantId;
    destination.disabledForOwner = source.disabledForOwner;
    destination.ownerCanEditStatus = source.ownerCanEditStatus;
    destination.originalStatus = source.originalStatus;
    destination.appInstanceId = source.appInstanceId;
    destination.targetEnvironment = source.targetEnvironment;
    destination.failedDate = source.failedDate;
    destination.failedReason = source.failedReason;
    destination.ownerConsentDate = source.ownerConsentDate;
    destination.ownerApprovalDate = source.ownerApprovalDate;
    destination.entraAppEnv = source.entraAppEnv;
    destination.statusChangedDate = source.statusChangedDate;
    destination.appDelegate1 = source.appDelegate1;
    destination.appDelegate2 = source.appDelegate2;
    destination.entraIdProjectStatus = source.entraIdProjectStatus;
    destination.statusHistory = source.statusHistory;
};

export const optionsTrueFalseEmpty = ['True', 'False', 'Empty'];